import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Download as DownloadIcon, Visibility as ViewIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';

interface Batch {
  id: number;
  list_name: string;
  status: string;
  count: number;
  created_at: string;
}

interface SavedResult {
  id: number;
  list_name: string;
  domain: string;
  url: string;
  title: string;
  description: string;
  created_at: string;
}

interface DeepSearchResult {
  id: number;
  list_name: string;
  domain: string;
  title: string;
  description: string;
  source_query: string;
  created_at: string;
}

const SavedResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [savedResults, setSavedResults] = useState<SavedResult[]>([]);
  const [deepSearchResults, setDeepSearchResults] = useState<DeepSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<any[]>([]);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await ApiService.getSearchListBatches();
      const batchList = (res || []).map((row, i) => ({
        id: i,
        list_name: row.list_name,
        status: row.status || 'active',
        count: Number(row.count) || 0,
        created_at: row.created_at,
      }));
      setBatches(batchList);
    } catch (err) {
      console.error('Failed to load batches:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedResults = async () => {
    setLoading(true);
    try {
      const res = await ApiService.getSavedResults();
      setSavedResults(res);
    } catch (err) {
      console.error('Failed to load saved results:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDeepSearchResults = async () => {
    setLoading(true);
    try {
      const res = await ApiService.getDeepSearchResults();
      setDeepSearchResults(res);
    } catch (err) {
      console.error('Failed to load deep search results:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) loadBatches();
    else if (tab === 1) loadSavedResults();
    else if (tab === 2) loadDeepSearchResults();
  }, [tab]);

  const exportToCSV = (data: any[], filename: string) => {
    if (!data.length) {
      alert('No data to export');
      return;
    }
    const headers = Object.keys(data[0]);
    const csv = [headers.join(','), ...data.map(r => headers.map(h => JSON.stringify(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openViewDialog = (listName: string, dataType: 'saved' | 'deep') => {
    setSelectedList(listName);
    if (dataType === 'saved') {
      setViewData(savedResults.filter(r => r.list_name === listName));
    } else {
      setViewData(deepSearchResults.filter(r => r.list_name === listName));
    }
    setViewDialogOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Queries, Lists & Captured Data</Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Batches" />
          <Tab label="Flagged Results (Triangles)" />
          <Tab label="Deep Search Results" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Batch Name</TableCell>
                <TableCell align="center">Rows</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{b.list_name}</TableCell>
                  <TableCell align="center">{b.count}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={b.status}
                      color={b.status === 'done' ? 'default' : 'success'}
                      variant={b.status === 'done' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{new Date(b.created_at).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/outreach/search?list=${encodeURIComponent(b.list_name)}`)}
                      sx={{ mr: 1 }}
                    >
                      Open
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportToCSV(batches.filter(x => x.list_name === b.list_name), `batch-${b.list_name}`)}
                    >
                      Export
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No batches yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : tab === 1 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>List</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {savedResults.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.list_name}</TableCell>
                  <TableCell>{r.domain}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Button size="small" href={r.url} target="_blank" variant="text">
                      Visit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {savedResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No flagged results yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>List</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Query</TableCell>
                <TableCell>Title</TableCell>
                <TableCell align="center">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Group by list_name + source_query to show unique domain counts */}
              {(() => {
                const grouped: Record<string, Record<string, DeepSearchResult[]>> = {};
                deepSearchResults.forEach(r => {
                  if (!grouped[r.list_name]) grouped[r.list_name] = {};
                  if (!grouped[r.list_name][r.source_query]) grouped[r.list_name][r.source_query] = [];
                  grouped[r.list_name][r.source_query].push(r);
                });
                const rows: any[] = [];
                Object.entries(grouped).forEach(([list, queries]) => {
                  Object.entries(queries).forEach(([query, results]) => {
                    rows.push({ list, query, count: results.length, results });
                  });
                });
                return rows.length ? (
                  rows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.list}</TableCell>
                      <TableCell colSpan={2}>{row.query}</TableCell>
                      <TableCell />
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ViewIcon />}
                          onClick={() => openViewDialog(row.list, 'deep')}
                        >
                          {row.count}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No deep search results yet
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedList} — {viewData.length} results</DialogTitle>
        <DialogContent sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {viewData.map((r, i) => (
            <Box key={i} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.domain || r.displayLink}
              </Typography>
              {r.title && <Typography variant="caption" color="text.secondary">{r.title}</Typography>}
              {r.description && <Typography variant="caption" display="block" color="text.secondary">{r.description}</Typography>}
              {r.url && (
                <Button size="small" href={r.url} target="_blank" variant="text" sx={{ mt: 0.5 }}>
                  Visit URL
                </Button>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => exportToCSV(viewData, `results-${selectedList}`)}>Export as CSV</Button>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavedResultsPage;
